import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';

import { checkShouldFinishSession } from './sessionHelpers';
import { audioEnd } from './audioSlice';
import { hearingInfoReceived } from './callEvents';
import { initialCallState } from './callInitialState';
import {
  callIdSelector,
  selectActualCallOwnerUserId,
  isConferenceCall,
  selectHasReplacedCallOwner,
} from './selectors';
import { checkAndUpdateCallee } from './thunks';
import {
  recordPrimaryHearingConnectThunk,
  recordPrimaryHearingDisconnectThunk,
  recordTwoLineVCOConnectThunk,
  recordTwoLineVCODisconnectThunk,
} from './reportingThunks';
import { waitForCallerSessionDisconnect } from './waitForDisconnectThunk';

import { AppSupportedLanguage } from 'config/enums';
import {
  logAnswerToSdpStatus,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';
import { setInnerLoading } from 'features/app/store/app/appSlice';
import type { RootState } from 'features/app/store/store';
import { AudioPlayer } from 'features/audio-player/services';
import {
  CallType,
  ConnectionChangeType,
  LogoutType,
  TerminationType,
  type RelayCallState,
} from 'features/call/call-base/enums';
import {
  adaptIncomingCall,
  closeCallAnalytics,
  denyCallAnalytics,
  removePlusFromPhoneNumber,
  takeCallAnalytics,
  formatPhoneNumberForCalleeUpdate,
} from 'features/call/call-base/helpers';
import type {
  Call,
  CallStarvationTimeoutPayload,
  CloseCallPayload,
  ConnectionStateHearingConnectPayload,
  ConnectionStateHearingDisconnectPayload,
  ConnectionStatePayload,
  DenyCallWhileInProgressPayload,
} from 'features/call/call-base/interfaces';
import { CallDaoService } from 'features/call/call-base/services';
import { resetConference } from 'features/call/call-conference/store';
import { resetCallCaptions } from 'features/call/call-captions';
import {
  isDeafConnectedSelector,
  resetCallDeaf,
  resetDeafAudio,
} from 'features/call/call-deaf/store';
import { resetCallDetails } from 'features/call/call-details/store';
import {
  HearingParticipantPhoneNumberType,
  HearingParticipantType,
} from 'features/call/call-hearing/interfaces';
import {
  getHearingById,
  getNotDisconnectedHearingByPhoneNumberSelector,
  isPrimaryHearingConnectedSelector,
  resetCallHearing,
} from 'features/call/call-hearing/store';
import { resetToIdle } from 'features/call/call-ui-state/store';
import { resetVrsCall } from 'features/call/vrs-call/store';
import { resetVrsConferenceCall } from 'features/call/vrs-conference-call/store';
import { callerSessionAnalyticsInfo } from 'features/caller-session/helpers';
import {
  incomingCallAccepted,
  resetCallerSession,
  startedTakingCalls,
  stoppedTakingCalls,
} from 'features/caller-session/store';
import { resetColdHandoff } from 'features/cold-handoff/store';
import { resetCustomGreeting } from 'features/custom-greeting/store';
import { resetDeafPhoneNumberHiddenStatus } from 'features/deaf-phone-number-hidden/store';
import { resetDialPad } from 'features/dial-pad/store';
import { resetDialedNumbersHistory } from 'features/dialed-numbers-history/store';
import { resetEmergency, markAsEmergencyCall } from 'features/emergency/store';
import { RecentCallsTracker } from 'features/hidden-debug-menu/components/';
import { isVriModeSelector, resetMultiMode } from 'features/multi-mode/store';
import { handleError } from 'features/notification/store';
import {
  hearingConnectRecordedSelector,
  resetReportingState,
  sdpReceivedSelector,
  twoLineVCORecordedSelector,
} from 'features/reporting/store';
import {
  finishSession,
  removeSession,
  rnsConnectionIdSelector,
  sessionIdSelector,
  answerTimeoutOccurred,
  resetAnswerTimeoutCount,
} from 'features/session/store';
import { _resetSignMail } from 'features/signmail/store';
import { disableSpawnCall } from 'features/spawn-call/store';
import { resetRemoteVideoIp } from 'features/stats/store';
import { resetTeaming } from 'features/teaming/teaming-base/store';
import { UserStatus } from 'features/user/enums';
import { userIdSelector } from 'features/user/store';
import { toIsoWithTimezone } from 'features/utils/helpers';
import { isPrimaryVcoSenderSelector, resetVco } from 'features/vco/store';
import { resetVideoPrivacy } from 'features/video-privacy/store';
import { getConnectionChangeTypeByDisconnectReason } from 'features/voice-meeting/helpers';
import { resetVoiceSession } from 'features/voice-session/store';
import { resetVrsChatState } from 'features/vrs-chat/store';
import { handleServiceResets } from 'features/call/call-events/helpers/';
import { LeaveQueueCompletionBehavior } from 'features/leave-queue/enums';
import { RtcSessionTracker } from 'features/sip/services';
import { VoiceMeetingService } from 'features/voice-meeting/services/';
import { leaveQueue } from 'features/leave-queue/store';
import { resetTrainingForker } from 'features/training-forker/store';

export type { CallState } from './callInitialState';
export { initialCallState } from './callInitialState';

export const callSlice = createSlice({
  name: 'call',
  initialState: initialCallState,
  reducers: {
    setReceivedCall: (state, action: PayloadAction<Call>) => {
      const callData = adaptIncomingCall(action.payload);
      const newState = {
        ...state,
        ...callData,
      };
      if (callData.callType === CallType.EMERGENCY_CALL) {
        newState.isAutoCompleteEnabled = false;
      }
      return newState;
    },
    setUpdatedCall: (state, action: PayloadAction<Call>) => {
      const callData = adaptIncomingCall(action.payload);
      const newState = {
        ...state,
        ...callData,
      };
      if (callData.callType === CallType.EMERGENCY_CALL) {
        newState.isAutoCompleteEnabled = false;
      }
      return newState;
    },
    setCallType: (state, action: PayloadAction<CallType | null>) => {
      state.callType = action.payload;
      if (action.payload === CallType.EMERGENCY_CALL) {
        state.isAutoCompleteEnabled = false;
      }
    },
    setCallTakenTime: (state, action: PayloadAction<string>) => {
      state.callTakenTime = action.payload;
    },
    callOwnerTransferred: (
      state,
      action: PayloadAction<{
        userId: number;
        name: string;
      }>
    ) => {
      state.replacedCallOwnerUserId = action.payload.userId;
      state.replacedCallOwnerName = action.payload.name;
    },
    resetCall: (state) => {
      const resetState = { ...initialCallState };

      if (state.lastCallId !== undefined) {
        resetState.lastCallId = state.lastCallId;
      }

      return resetState;
    },
    setRelayCallState: (state, action: PayloadAction<RelayCallState>) => {
      state.relayCallState = action.payload;
    },
    toggleAutoComplete: (state) => {
      state.isAutoCompleteEnabled = !state.isAutoCompleteEnabled;
    },
    setCallId: (state, action: PayloadAction<number>) => {
      state.id = action.payload;
    },
    setRegistrationType: (state, action: PayloadAction<number | null>) => {
      state.registrationType = action.payload;
    },
    setTerminationType: (state, action: PayloadAction<TerminationType>) => {
      state.terminationType = action.payload;
    },
    setSipCallId: (state, action: PayloadAction<string>) => {
      state.sipCallId = action.payload;
    },
    setSipHeaderFrom: (state, action: PayloadAction<string>) => {
      state.sipHeaderFrom = action.payload;
    },
    setLastCallId: (state, action: PayloadAction<number>) => {
      state.lastCallId = action.payload;
    },
    setPeerUserAgent: (state, action: PayloadAction<string>) => {
      state.peerUserAgent = action.payload;
    },
    requiredApiCallsPending: (state) => {
      state.connectionStateHistoryCount++;
    },
    finishRequiredApiCallsPending: (state) => {
      state.connectionStateHistoryCount--;
    },
    updateCalleeConnectionString: (state, action: PayloadAction<string>) => {
      state.calleeConnectionString = action.payload;
    },
    deafIpTracebackKnown: (state, action: PayloadAction<string>) => {
      state.DeafIpTraceback = action.payload;
    },
    markCallAsAbandoned: (state) => {
      state.wasCallAbandoned = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(hearingInfoReceived, (state, action) => {
      const hearingInitiatedCallInfo = action.payload.HearingInitiatedCallInfo;
      const call = action.payload.Call;

      state.registrationType =
        call?.RegistrationType ?? action.payload.RegistrationType;
      state.calleeConnectionString =
        call?.CalleeConnectionString ?? state.calleeConnectionString;

      if (!hearingInitiatedCallInfo) {
        return;
      }

      const {
        LanguageCode,
        CallerName,
        RegistrationFailureCode,
        VcoPreference,
        CalleeCoreMyPhoneGroupOwnerId,
        CalleeCoreUserId,
      } = hearingInitiatedCallInfo;

      Object.assign(state, {
        languageCode: LanguageCode ?? state.languageCode,
        callerName: CallerName ?? state.callerName,
        registrationFailureCode:
          RegistrationFailureCode ?? state.registrationFailureCode,
        vcoPreference: VcoPreference ?? state.vcoPreference,
        calleeCoreMyPhoneGroupOwnerId:
          CalleeCoreMyPhoneGroupOwnerId ?? state.calleeCoreMyPhoneGroupOwnerId,
        calleeCoreUserId: CalleeCoreUserId ?? state.calleeCoreUserId,
      });
    });
    builder.addMatcher(
      (action) =>
        action.type.startsWith('call/') && action.type.includes('fulfilled'),
      (state) => {
        if (state.callType === CallType.EMERGENCY_CALL) {
          state.isAutoCompleteEnabled = false;
        }
      }
    );
    builder.addMatcher(markAsEmergencyCall.match, (state) => {
      setCallType(CallType.EMERGENCY_CALL);
      state.isAutoCompleteEnabled = false;
    });
  },
});

export const {
  setReceivedCall,
  setUpdatedCall,
  deafIpTracebackKnown,
  setCallType,
  setCallTakenTime,
  callOwnerTransferred,
  resetCall,
  setRelayCallState,
  setCallId,
  setRegistrationType,
  setTerminationType,
  setSipCallId,
  setSipHeaderFrom,
  setLastCallId,
  setPeerUserAgent,
  toggleAutoComplete,
  requiredApiCallsPending,
  finishRequiredApiCallsPending,
  updateCalleeConnectionString,
  markCallAsAbandoned,
} = callSlice.actions;

export const callReducer = callSlice.reducer;

// ========== EXPORTED THUNKS ==========

export const handleResetCall = createAsyncThunk(
  'call/reset',
  async (_, { dispatch, getState }) => {
    // Tear down Chime voice meeting session if active.
    VoiceMeetingService.getInstance().completeMeetingSession();

    RtcSessionTracker.disconnectAndClearAll();
    const state = getState() as RootState;
    const shouldForceCognitoSignOut = selectHasReplacedCallOwner(state);
    let forcedLogoutStarted = false;

    try {
      // Dispatch the wait thunk. Redux Toolkit dispatch returns the action
      // object for async thunks (fulfilled or rejected). We must detect a
      // rejected action and throw to preserve the original behavior where
      // waitForSelector would throw on timeout.
      await dispatch(waitForCallerSessionDisconnect()).unwrap();
    } catch (err) {
      console.log(err);
      // Temporary solution until we can handle this in the caller session UI enhancement
      dispatch(
        handleError({
          error: new Error('Caller session not endable'),
          title: t('callErrors.callPossiblyStillConnectedTitle'),
          message: t('callErrors.callPossiblyStillConnectedMessage'),
        })
      );

      if (!shouldForceCognitoSignOut) {
        throw new Error('Caller session not endable');
      }
    }

    if (shouldForceCognitoSignOut) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'handleResetCall',
          Message:
            'Forcing user logout due to replaced call owner on in person teaming',
        })
      );
      await dispatch(
        leaveQueue({
          shouldFinishSession: true,
          completionBehavior: LeaveQueueCompletionBehavior.TeamingLogoutNotice,
        })
      );
      forcedLogoutStarted = true;
    }
    await dispatch(performResets()).unwrap();

    return {
      forcedLogoutStarted,
    };
  }
);

export const ensureCallStateReset = createAsyncThunk(
  'call/ensureCallStateReset',
  async (_, { dispatch, getState }) => {
    const { selectShouldEnforceCallStateResetBoundary } =
      await import('./callResetBoundarySlices');

    const shouldReset = () =>
      selectShouldEnforceCallStateResetBoundary(getState() as RootState);

    const runResetPass = async () => {
      await dispatch(handleResetCall()).unwrap();
    };

    if (!shouldReset()) {
      return;
    }

    await runResetPass();
  }
);

export const answerTimeoutResetCall = createAsyncThunk(
  'call/answerTimeoutReset',
  async (_, { dispatch }) => {
    RtcSessionTracker.disconnectAndClearAll();
    await dispatch(performResets()).unwrap();
  }
);

const performResets = createAsyncThunk(
  'call/performResets',
  (_, { dispatch }) => {
    dispatch(resetCall());
    dispatch(resetCallCaptions());
    dispatch(resetTeaming());
    dispatch(resetDeafPhoneNumberHiddenStatus());
    dispatch(resetCallHearing());
    dispatch(resetVideoPrivacy());
    dispatch(audioEnd());
    dispatch(resetDialPad());
    dispatch(resetDialedNumbersHistory());
    dispatch(resetVco());
    dispatch(resetConference());
    dispatch(resetCallDetails());
    dispatch(resetVrsCall());
    dispatch(resetColdHandoff());
    dispatch(resetEmergency());
    dispatch(resetCustomGreeting());
    dispatch(resetVrsConferenceCall());
    dispatch(resetDeafAudio());
    dispatch(resetRemoteVideoIp());
    dispatch(resetCallDeaf());
    dispatch(resetVoiceSession());
    dispatch(resetVrsChatState());
    dispatch(resetReportingState());
    dispatch(_resetSignMail());
    dispatch(resetCallerSession());
    dispatch(resetToIdle());
    dispatch(resetTrainingForker());
  }
);

export const handleReceivedCall = createAsyncThunk(
  'call/handleReceivedCall',
  async (call: Call, { dispatch, getState }) => {
    // Set the received call with original data
    const isVri = isVriModeSelector(getState() as RootState);
    dispatch(setReceivedCall(call));

    if (isVri) {
      // For VRI calls, no need to update callee number
      return;
    }

    // Check if callee phone number needs format conversion (e.g., +52 → 011)
    const originalNumber = call.CalleeConnectionString;
    const formattedNumber = formatPhoneNumberForCalleeUpdate(originalNumber);

    const needsConvertingForBilling =
      originalNumber && formattedNumber && originalNumber !== formattedNumber;

    if (!needsConvertingForBilling) {
      return;
    }

    // Log the conversion
    dispatch(
      sendAnalyticsInfo({
        Method: 'handleReceivedCall',
        Message: `Converting CalleeConnectionString from ${originalNumber} to ${formattedNumber}`,
      })
    );

    // Update local state
    dispatch(updateCalleeConnectionString(formattedNumber));

    // Update backend
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);

      await CallDaoService.updateCalleePhoneNumber({
        callId,
        phoneNumber: formattedNumber,
      });

      dispatch(
        sendAnalyticsInfo({
          Method: 'handleReceivedCall',
          Message: `Successfully updated CalleeConnectionString to ${formattedNumber}`,
        })
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleReceivedCall',
          message: 'Failed to update callee connection string in backend',
        })
      );
    }
  }
);

export const closeCall = createAsyncThunk(
  'call/close',
  async (
    payload: CloseCallPayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    const callManager = RecentCallsTracker.getInstance();
    const isAnswerTimeout =
      payload.terminationType === TerminationType.AnswerTO;

    // Log SDP status for AnswerTO cases
    if (isAnswerTimeout) {
      const state = getState() as RootState;
      const sdpReceived = sdpReceivedSelector(state);
      logAnswerToSdpStatus(dispatch, sdpReceived);
    }

    try {
      dispatch(setInnerLoading(true));

      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const userId = selectActualCallOwnerUserId(state);
      const abandonAfterTaken = state.call.wasCallAbandoned;
      const terminationType = TerminationType[payload.terminationType];
      const isDeafToHearing = state.call.isDeafToHearing;

      if (!callId) {
        dispatch(
          sendAnalyticsInfo({
            Method: 'closeCall',
            Message:
              'Skipped closeCall — no valid callId. Call was likely already reset by disconnect handler.',
          })
        );
        return;
      }

      await CallDaoService.closeCall({
        callId,
        userId,
        terminationType: payload.terminationType,
        abandonAfterTaken,
      });

      dispatch(setLastCallId(callId));
      callManager.addCallLog(callId, payload.terminationType);

      dispatch(
        closeCallAnalytics(
          'closeCall',
          abandonAfterTaken,
          terminationType,
          isDeafToHearing
        )
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'closeCall',
        })
      );
      return rejectWithValue(error);
    } finally {
      if (isAnswerTimeout) {
        dispatch(answerTimeoutResetCall());
      } else {
        // Reset answer timeout count when call ends normally (not due to answer timeout)
        dispatch(resetAnswerTimeoutCount());
        dispatch(handleResetCall()).unwrap();
      }
      dispatch(disableSpawnCall());
      dispatch(setInnerLoading(false));
    }
  }
);

let isDenyCallPending = false;

export const denyCall = createAsyncThunk(
  'call/deny',
  async (_, { dispatch, getState, rejectWithValue }) => {
    if (isDenyCallPending) {
      return;
    }
    isDenyCallPending = true;
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      dispatch(stoppedTakingCalls());
      const logoutType = LogoutType[LogoutType.Normal];
      await CallDaoService.denyCall({ callId });
      dispatch(denyCallAnalytics('denyCall', logoutType));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'denyCall',
        })
      );
      return rejectWithValue(error);
    } finally {
      isDenyCallPending = false;
      await dispatch(handleResetCall()).unwrap();
      AudioPlayer.stop();
    }
  }
);

export const resetCallSession = createAsyncThunk(
  'call/resetCallSession',
  async (_, { dispatch }) => {
    try {
      dispatch(
        sendAnalyticsInfo({
          Method: 'resetCallSession',
          Message: 'Reset call session',
        })
      );
      await dispatch(removeSession({ logoutType: LogoutType.Normal })).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'resetCallSession',
        })
      );
    } finally {
      dispatch(resetMultiMode());
      dispatch(disableSpawnCall());
    }
  }
);

export const denyCallWhileInProgress = createAsyncThunk(
  'call/denyCallWhileInProgress',
  async (
    { callId }: DenyCallWhileInProgressPayload,
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const isDeafConnected = isDeafConnectedSelector(state);
      const isHearingConnected = isPrimaryHearingConnectedSelector(state);

      const userStatus =
        isDeafConnected && isHearingConnected
          ? UserStatus.Intrptng
          : UserStatus.InPrgres;

      return await CallDaoService.denyCall({
        callId,
        userStatus,
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const shouldCallBeAnswerTimeout = (state: RootState) => {
  const isDeafConnected = isDeafConnectedSelector(state);
  const isHearingConnected = isPrimaryHearingConnectedSelector(state);
  if (isDeafConnected || isHearingConnected) {
    return false;
  }
  return true;
};

export const handleCallNotReceived = createAsyncThunk(
  'call/handleNotReceived',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;

    // Log the call not received error for user feedback
    dispatch(
      handleError({
        error: t('callNoLongerAvailable', { lng: AppSupportedLanguage.EN }),
        title: t('callNoLongerAvailable'),
        methodName: 'handleCallNotReceived',
        state: { ...state.call, ...state.callDeaf },
        ignoreReporting: true,
      })
    );

    dispatch(markCallAsAbandoned());

    // Close the call with answer timeout termination
    await dispatch(
      closeCall({
        terminationType: TerminationType.AnswerTO,
      })
    );

    // Process consecutive answer timeout logic
    await dispatch(handleAnswerTimeout());
  }
);

/**
 * Handles answer timeout logic by:
 * 1. Incrementing the consecutive answer timeout count
 * 2. Checking if the threshold has been reached
 * 3. Finishing the session if threshold exceeded
 */
export const handleAnswerTimeout = createAsyncThunk(
  'call/handleAnswerTimeout',
  async (_, { dispatch, getState }) => {
    // Increment the consecutive answer timeout count
    dispatch(answerTimeoutOccurred());

    // Check if session should be finished due to consecutive timeouts
    const shouldFinishSession = checkShouldFinishSession(getState, dispatch);

    if (shouldFinishSession) {
      await dispatch(finishSession({ logoutType: LogoutType.Normal })).unwrap();
      dispatch(
        sendAnalyticsInfo({
          Method: 'handleAnswerTimeout',
          Message: 'Session finished due to consecutive answer timeouts',
          Event: 'SessionFinishedDueToConsecutiveAnswerTimeouts',
        })
      );
    }
  }
);

export const takeCall = createAsyncThunk(
  'call/take',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const userId = userIdSelector(state);
      const rnsSessionId = rnsConnectionIdSelector(state);

      dispatch(takeCallAnalytics('takeCall'));
      const call = await CallDaoService.takeCall({
        callId,
        userId,
        sessionId: rnsSessionId,
      });

      if (!call) {
        throw new Error('Call returned no object');
      }
      dispatch(
        incomingCallAccepted(callId.toString(), call.CallerConnectionString)
      );
      dispatch(callerSessionAnalyticsInfo('Accepted incoming call'));
      dispatch(setRelayCallState(call.CallState));
      dispatch(
        setCallTakenTime(toIsoWithTimezone(call.TakenOrAbandonDateTime))
      );
      return call;
    } catch (error) {
      dispatch(
        handleError({
          title: 'Call no longer available',
          message: 'Returning to the queue for next call',
          error,
          methodName: 'takeCall',
        })
      );
      handleServiceResets();
      await dispatch(handleResetCall()).unwrap();
      dispatch(startedTakingCalls());
      dispatch(setInnerLoading(false));
      return rejectWithValue(error);
    } finally {
      AudioPlayer.stop();
    }
  }
);

export const sendConnectionCallState = createAsyncThunk(
  'call/sendConnectionCallState',
  async (payload: ConnectionStatePayload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const isDeafConnected = isDeafConnectedSelector(state);
    const isHearingConnected = isPrimaryHearingConnectedSelector(state);
    const sessionLoginHistoryId = sessionIdSelector(state);
    const isPrimaryVcoSender = isPrimaryVcoSenderSelector(state);
    try {
      if (!isPrimaryVcoSender || !callId) {
        return;
      }

      dispatch(requiredApiCallsPending());
      const call = await CallDaoService.connectionStateHistory({
        ...payload,
        callId,
        isDeafConnected: payload.isDeafConnected ?? isDeafConnected,
        isHearingConnected,
        sessionLoginHistoryId,
      });

      dispatch(setRelayCallState(call.CallState));

      await dispatch(checkAndUpdateCallee()).unwrap();

      return call;
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'sendConnectionCallState',
          title: t('callErrors.updateCallState', { callId }),
        })
      );
    } finally {
      dispatch(finishRequiredApiCallsPending());
    }
  }
);

export const sendConnectionCallStateHearingConnect = createAsyncThunk(
  'call/sendConnectionCallHearingConnect',
  async (
    {
      phoneNumber,
      interpretingSessionType,
    }: ConnectionStateHearingConnectPayload,
    { dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      // Get participant details
      const hearingParticipant = getParticipantDetails(state, phoneNumber);
      if (!hearingParticipant) {
        return; // No participant found or is secondary
      }

      // Handle VCO and primary connect recordings
      await handleConnectRecordings(
        state,
        dispatch,
        hearingParticipant,
        interpretingSessionType
      );

      // Update connection state based on participant type and direction
      await updateConnectionState(
        state,
        dispatch,
        hearingParticipant,
        interpretingSessionType
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'sendConnectionCallStateHearingConnect',
        })
      );
    }
  }
);

export const sendConnectionCallStateHearingDisconnect = createAsyncThunk(
  'call/sendConnectionCallHearingDisconnect',
  async (
    {
      disconnectReason,
      interpretingSessionType,
      id,
    }: ConnectionStateHearingDisconnectPayload,
    { dispatch, getState }
  ) => {
    try {
      // Early return if no ID is provided
      if (!id) {
        return;
      }

      const state = getState() as RootState;

      // Get and validate participant
      const participantInfo = getDisconnectParticipantInfo(state, id);
      if (!participantInfo) {
        return;
      }

      // Handle disconnect recordings
      if (!handleDisconnectRecordings(state, dispatch, participantInfo)) {
        return;
      }

      // Send connection state update
      await sendDisconnectConnectionState(
        state,
        dispatch,
        participantInfo,
        disconnectReason,
        interpretingSessionType
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'sendConnectionCallStateHearingDisconnect',
        })
      );
    }
  }
);

export const callStarvationTimeout = createAsyncThunk(
  'call/starvationTimeout',
  async (payload: CallStarvationTimeoutPayload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);

    if (callId !== payload.receivedCallId) {
      return;
    }

    try {
      await CallDaoService.starvationReset(callId);
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'callStarvationTimeout',
        })
      );
    }
  }
);

export const updateCallRecord = createAsyncThunk(
  'call/updateCallRecord',
  async (payload: Partial<Call>, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);

      const call = await CallDaoService.updateCall(callId, payload);
      dispatch(setUpdatedCall(call));

      return call;
    } catch (error) {
      dispatch(handleError({ error, methodName: 'updateCallRecord' }));
    }
  }
);

export const getCallState = createAsyncThunk(
  'call/getCallState',
  async (_, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);

      const call = await CallDaoService.getCall(callId);
      dispatch(setRelayCallState(call.CallState));
    } catch (error) {
      dispatch(handleError({ error, methodName: 'getCallState' }));
    }
  }
);

// ========== HELPER FUNCTIONS ==========

// Helper function to get participant details and perform initial checks
const getParticipantDetails = (state: RootState, phoneNumber: string) => {
  const isHearingCallerIdBlocked =
    phoneNumber === HearingParticipantPhoneNumberType.ANONYMOUS;

  const participant = isHearingCallerIdBlocked
    ? null
    : getNotDisconnectedHearingByPhoneNumberSelector(
        state,
        removePlusFromPhoneNumber(phoneNumber)
      );

  // For anonymous calls, create a virtual participant for processing
  const hearingParticipant =
    participant ||
    (isHearingCallerIdBlocked
      ? { type: HearingParticipantType.PRIMARY, direction: 'inbound' }
      : null);

  if (!hearingParticipant) {
    return null;
  }

  // Early return for secondary participants
  if (hearingParticipant.type === HearingParticipantType.SECONDARY) {
    return null;
  }

  return {
    ...hearingParticipant,
    isHearingCallerIdBlocked,
    isInbound: hearingParticipant.direction === 'inbound',
    isOutbound: hearingParticipant.direction !== 'inbound',
    isTwoLineVCO: hearingParticipant.type === HearingParticipantType.VCO2LINE,
    isPrimary: hearingParticipant.type === HearingParticipantType.PRIMARY,
  };
};

// Helper function to handle VCO and primary connect recordings
const handleConnectRecordings = async (
  state: RootState,
  dispatch: any,
  participant: any,
  interpretingSessionType: any
) => {
  const { isTwoLineVCO, isPrimary, isHearingCallerIdBlocked } = participant;
  const isPrimaryHearingConnectReported = hearingConnectRecordedSelector(state);
  const isTwoLineVCOConnectReported = twoLineVCORecordedSelector(state);

  // Handle two-line VCO connect
  if (isTwoLineVCO && !isTwoLineVCOConnectReported) {
    await dispatch(
      sendConnectionCallState({
        interpretingSessionType,
        connectionChangeType: ConnectionChangeType.AGENT_CONNECT_VCO,
      })
    ).unwrap();
    dispatch(recordTwoLineVCOConnectThunk());
  }

  // Handle primary hearing connect for billing
  if (
    isPrimary ||
    (isHearingCallerIdBlocked && !isPrimaryHearingConnectReported)
  ) {
    dispatch(recordPrimaryHearingConnectThunk());
  }
};

// Helper function to update connection state
const updateConnectionState = async (
  state: RootState,
  dispatch: any,
  participant: any,
  interpretingSessionType: any
) => {
  const { isPrimary, isOutbound, isInbound, isHearingCallerIdBlocked } =
    participant;
  const isConferenceCallType = isConferenceCall(state);

  // Common connection parameters
  const connectionParams = {
    interpretingSessionType,
    ...(isConferenceCallType ? { isDeafConnected: true } : {}),
  };

  // Outbound primary connection
  if (isPrimary && isOutbound) {
    await dispatch(
      sendConnectionCallState({
        ...connectionParams,
        connectionChangeType: ConnectionChangeType.AGENT_CONNECT_HEARING,
      })
    ).unwrap();
    return;
  }

  // Inbound primary or anonymous connection
  if (isInbound && (isPrimary || isHearingCallerIdBlocked)) {
    await dispatch(
      sendConnectionCallState({
        ...connectionParams,
        connectionChangeType: ConnectionChangeType.HEARING_CONNECT,
      })
    ).unwrap();
  }
};

// Helper function to get and validate participant information
const getDisconnectParticipantInfo = (state: RootState, id: string) => {
  const hearingParticipant = getHearingById(state, id);
  if (!hearingParticipant) {
    return null;
  }

  // Extract participant properties
  const type = hearingParticipant.type;
  const isTwoLineVco = type === HearingParticipantType.VCO2LINE;
  const isPrimaryHearing = type === HearingParticipantType.PRIMARY;
  const isSecondaryHearing = type === HearingParticipantType.SECONDARY;

  // Early return for secondary hearing participants
  if (isSecondaryHearing) {
    return null;
  }

  return {
    isTwoLineVco,
    isPrimaryHearing,
    hearingParticipant,
  };
};

// Helper function to handle disconnect recordings
const handleDisconnectRecordings = (
  state: RootState,
  dispatch: any,
  participantInfo: {
    isTwoLineVco: boolean;
    isPrimaryHearing: boolean;
  }
) => {
  const { isTwoLineVco, isPrimaryHearing } = participantInfo;
  const vco2LineConnectRecorded = twoLineVCORecordedSelector(state);
  const hearingConnectRecorded = hearingConnectRecordedSelector(state);

  // Handle Two-Line VCO disconnect
  if (isTwoLineVco) {
    if (!vco2LineConnectRecorded) {
      return false;
    }
    dispatch(recordTwoLineVCODisconnectThunk());
  }

  // Handle Primary Hearing disconnect
  if (isPrimaryHearing) {
    if (!hearingConnectRecorded) {
      return false;
    }
    dispatch(recordPrimaryHearingDisconnectThunk());
  }

  return true;
};

// Helper function to send disconnect connection state
const sendDisconnectConnectionState = async (
  state: RootState,
  dispatch: any,
  participantInfo: {
    isTwoLineVco: boolean;
  },
  disconnectReason: any,
  interpretingSessionType: any
) => {
  const { isTwoLineVco } = participantInfo;
  const isConferenceCallType = isConferenceCall(state);

  // Determine connection change type for the disconnect
  const connectionChangeType = getConnectionChangeTypeByDisconnectReason(
    isTwoLineVco,
    disconnectReason
  );

  await dispatch(
    sendConnectionCallState({
      interpretingSessionType,
      connectionChangeType,
      ...(isConferenceCallType ? { isDeafConnected: true } : {}),
    })
  ).unwrap();
};
